import type { Price as StorePrice, Product } from '@haus-storefront-react/shared-types'
import { ProductCard } from '../shared/ProductCard'
import type { ProductListConfigStyles } from '../product-list/_propsFn'
import { useMemo } from 'react'

interface RelatedProductsItemProps {
  product: Product
  showCta?: boolean
  showInStock?: boolean
  showPrice?: boolean
  configStyles?: ProductListConfigStyles
}

function getNumericPrice(value: StorePrice): number | null {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value === 'object' && value !== null && 'value' in value) {
    const singlePrice = value.value
    return typeof singlePrice === 'number' ? singlePrice : null
  }

  return null
}

/**
 * Render a related product card reusing the existing product list card styling.
 */
export function RelatedProductsItem({
  product,
  showCta,
  showInStock,
  showPrice,
  configStyles,
}: RelatedProductsItemProps) {
  const firstVariant = product.variants[0]
  const inStock = firstVariant ? firstVariant.stockLevel !== 'OUT_OF_STOCK' : undefined

  const calculatedPrices = useMemo(() => {
    if (!firstVariant || product.variants.length === 0) {
      return { price: undefined, priceWithTax: undefined }
    }

    // Check if all variants have the same price. If so return the price of the first variant. If not, return a PriceRange with the min and max prices.
    const allVariantsHaveSamePrice = product.variants.every(
      (variant) => variant.price === firstVariant?.price,
    )
    if (allVariantsHaveSamePrice) {
      return { price: firstVariant?.price, priceWithTax: firstVariant?.priceWithTax }
    }

    const prices = product.variants
      .map((variant) => getNumericPrice(variant.price))
      .filter((value): value is number => value !== null)
    const pricesWithTax = product.variants
      .map((variant) => getNumericPrice(variant.priceWithTax))
      .filter((value): value is number => value !== null)

    if (prices.length === 0 || pricesWithTax.length === 0) {
      return { price: firstVariant.price, priceWithTax: firstVariant.priceWithTax }
    }

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const minPriceWithTax = Math.min(...pricesWithTax)
    const maxPriceWithTax = Math.max(...pricesWithTax)

    return {
      price: { min: minPrice, max: maxPrice },
      priceWithTax: { min: minPriceWithTax, max: maxPriceWithTax },
    }
  }, [firstVariant, product.variants])

  return (
    <ProductCard
      product={product}
      name={product.name}
      slug={product.slug}
      imageSrc={product.featuredAsset?.preview ?? undefined}
      price={calculatedPrices.price}
      priceWithTax={calculatedPrices.priceWithTax}
      listPrice={firstVariant?.listPrice}
      listPriceWithTax={firstVariant?.listPriceWithTax}
      currencyCode={firstVariant?.currencyCode ?? product.currencyCode}
      inStock={inStock}
      productVariantId={firstVariant?.id}
      showCta={showCta}
      showInStock={showInStock}
      showPrice={showPrice}
      configStyles={configStyles}
    />
  )
}
